import React, { useEffect, useMemo, useRef, useState } from 'react';
import { BlockView, BlockViewProps } from './block-view';
import { BlockDragPosition, BlockDragState, DividerRenderElement, Section } from './types';
import { useEditorStore } from './editor-store';
import { findClosestBoundary } from './helpers/block/find-closest-boundary';
import fastDeepEqual from 'fast-deep-equal';
import { DraggableData, DraggableEvent } from 'react-draggable';
import cloneDeep from 'lodash/cloneDeep';
import { filterMoveBlockBoundaries } from './layout-manager/filter-move-block-boundaries';
import { findBlock } from './helpers/find-block';
import { MemoizedDividerView } from './divider-view';
import { generateBlockId } from './helpers/generate-block-id';
import { useRefCallback } from 'src/utils/react/ref-callback.hook';
import { MemoizedEmptySectionView } from './empty-section-view';
import { useUpdateEffect } from 'react-use';
// import { memoizeRenderBlockView } from './utils/memoize-render-block-view';
import { shallow } from 'zustand/shallow';
import { createPropsEqualsChecker } from './utils/create-props-equals-checker';
import { MemoizedBlockViewContainer } from './block-view-container';
import { useAnimatedRenderElements } from './utils/animated-render-elements.hook';

const DRAG_THRESHOLD = 5;
const DRAG_ACTION_THRESHOLD = 60;
const DRAG_SELECT_THRESHOLD = 5;

function hasDraggedEnough(dx: number, dy: number): boolean {
  return Math.abs(dx) > DRAG_THRESHOLD || Math.abs(dy) > DRAG_THRESHOLD;
}

function hasDraggedEnoughForAction(dx: number, dy: number): boolean {
  return Math.abs(dx) > DRAG_ACTION_THRESHOLD || Math.abs(dy) > DRAG_ACTION_THRESHOLD;
}

function hasDraggedForSelect(dx: number, dy: number): boolean {
  return Math.abs(dx) < DRAG_SELECT_THRESHOLD && Math.abs(dy) < DRAG_SELECT_THRESHOLD;
}

export const MemoizedBlockView = React.memo<BlockViewProps>(
  BlockView,
  createPropsEqualsChecker(['style'], 'BlockView')
);

function createInitialDragState(): BlockDragState {
  return {
    blockId: undefined,
    dragged: false,
    draggedEnoughForAction: false,
    stopped: false,
  };
}

function createInitialDragPosition(): BlockDragPosition {
  return {
    startX: 0,
    startY: 0,
    dx: 0,
    dy: 0,
  };
}

export const AnimatedBlocksViewInner = () => {
  // const logPerf = createPerformanceLogger('AnimatedBlocksViewPerf');
  const layoutStage = useEditorStore((state) => state.layoutStage);
  const innerAreaRect = useEditorStore((state) => state.innerAreaRect);
  const canvasScrollPosition = useEditorStore((state) => state.canvasScrollPosition);
  const resizeDragState = useEditorStore((state) => state.resizeDragState);
  const focusedBlockId = useEditorStore((state) => state.focusedBlockId);
  const lastActionWasUndoOrRedo = useEditorStore((state) => state.lastActionWasUndoOrRedo);
  const reorderBoundary = useEditorStore((state) => state.reorderBoundary);
  const dispatchUserEditorAction = useEditorStore((state) => state.dispatchUserEditorAction);
  const selectBlock = useEditorStore((state) => state.selectBlock);
  const clearSelectedBlock = useEditorStore((state) => state.clearSelectedBlock);
  const setContextMenuState = useEditorStore((state) => state.setContextMenuState);
  const setScrollToBlockId = useEditorStore((state) => state.setScrollToBlockId);
  const setIsDraggingSomethingOnEditor = useEditorStore((state) => state.setIsDraggingSomethingOnEditor);
  const setReorderBoundary = useEditorStore((state) => state.setReorderBoundary);
  const journeyUUID = useEditorStore((state) => state.journeyUUID);

  const layout = useEditorStore((state) => state.layout, shallow);
  // Need this hack for now
  const handledLastActionWasUndoOrRedo = useRef(true);

  useUpdateEffect(() => {
    if (lastActionWasUndoOrRedo && handledLastActionWasUndoOrRedo.current) {
      handledLastActionWasUndoOrRedo.current = false;
    }
  }, [lastActionWasUndoOrRedo]);

  const { renderElements, sections, sectionGrids, boundaries } = layout;

  const [blockDragState, setBlockDragState] = useState<BlockDragState>(createInitialDragState());
  const blockDragPositionRef = useRef<BlockDragPosition>(createInitialDragPosition());

  const { sortedRenderElements, springs, startDragElement, dragElement, releaseElement } = useAnimatedRenderElements(
    renderElements,
    focusedBlockId,
    resizeDragState,
    lastActionWasUndoOrRedo,
    layoutStage
  );

  const onStart = useRefCallback(
    (id: string, e: DraggableEvent, args: DraggableData) => {
      // console.log('AnimatedBlocksView onStart', id, e, args);
      if (!innerAreaRect) {
        return;
      }
      const { x, y } = args;
      const dragState = {
        ...createInitialDragState(),
        blockId: id,
      };
      setBlockDragState(dragState);
      blockDragPositionRef.current = {
        ...createInitialDragPosition(),
        startX: x,
        startY: y,
      };
      const re = renderElements.find((re) => re.type === 'block' && re.block.id === id);
      if (re) {
        startDragElement(re.id, dragState, blockDragPositionRef.current);
      }
      setIsDraggingSomethingOnEditor(true);
      clearSelectedBlock();
    },
    [innerAreaRect, canvasScrollPosition]
  );

  const onDrag = useRefCallback(
    (id: string, e: DraggableEvent, args: DraggableData) => {
      // console.log('AnimatedBlocksView onDrag', id, e, args);
      if (!innerAreaRect) {
        return;
      }
      const { clientX, clientY } = e as MouseEvent;
      const { x, y } = args;
      const dx = x - blockDragPositionRef.current.startX;
      const dy = y - blockDragPositionRef.current.startY;
      const dragged = blockDragState.dragged || hasDraggedEnough(dx, dy);
      const draggedEnoughForAction = hasDraggedEnoughForAction(dx, dy);

      setBlockDragState((blockDragState) => {
        if (!fastDeepEqual(blockDragState, { dragged, draggedEnoughForAction, blockId: id, stopped: false })) {
          return {
            ...blockDragState,
            dragged,
            draggedEnoughForAction,
            blockId: id,
            stopped: false,
          };
        }
        return blockDragState;
      });
      blockDragPositionRef.current = {
        ...blockDragPositionRef.current,
        dx,
        dy,
      };
      const re = renderElements.find((re) => re.type === 'block' && re.block.id === id);
      if (re) {
        dragElement(
          re.id,
          {
            ...blockDragState,
            dragged,
            draggedEnoughForAction,
            blockId: id,
            stopped: false,
          },
          blockDragPositionRef.current
        );
      }

      const mouseY = clientY + canvasScrollPosition.y;
      const mouseX = clientX + canvasScrollPosition.x;

      const mouseRelativeX = mouseX - innerAreaRect.x;
      const mouseRelativeY = mouseY - innerAreaRect.y;

      const maybeReorderBoundary = findClosestBoundary({
        x: mouseRelativeX,
        y: mouseRelativeY,
        boundaries: filterMoveBlockBoundaries(boundaries, sectionGrids, id),
      });

      if (!fastDeepEqual(maybeReorderBoundary, reorderBoundary)) {
        setReorderBoundary(maybeReorderBoundary || null);
      }
    },
    [innerAreaRect, canvasScrollPosition, blockDragState, boundaries, sectionGrids, renderElements, reorderBoundary]
  );

  const onStop = useRefCallback(
    (id: string, e: DraggableEvent, args: DraggableData) => {
      // console.log('AnimatedBlocksView onStop', id, e, args);

      const { clientX, clientY } = e as MouseEvent;
      if (!innerAreaRect) {
        return;
      }

      const { dragged, draggedEnoughForAction, blockId } = blockDragState;
      const { dx, dy } = blockDragPositionRef.current;

      if (blockId) {
        if (!dragged || !draggedEnoughForAction) {
          // console.log('has not dragged enough');
          // console.log('TTT', dx, dy);
          if (hasDraggedForSelect(dx, dy)) {
            selectBlock(blockId, { x: clientX, y: clientY });
          }
        } else {
          // console.log('onStop updating');
          // if (gridWidth) {
          const block = findBlock(sections, id);
          const maybeReorderBoundary = findClosestBoundary({
            x: clientX + canvasScrollPosition.x - innerAreaRect.x,
            y: clientY + canvasScrollPosition.y - innerAreaRect.y,
            boundaries: filterMoveBlockBoundaries(boundaries, sectionGrids, id),
          });
          if (block && maybeReorderBoundary) {
            // console.log('updating block grid');
            setBlockDragState((blockDragState) => ({ ...blockDragState, stopped: true }));
            if (e.metaKey) {
              const id = generateBlockId();
              dispatchUserEditorAction({
                type: 'insert-block-at-boundary',
                id: id,
                content: cloneDeep(block.content),
                boundary: maybeReorderBoundary,
                layoutExtraParams: {},
              });
            } else {
              dispatchUserEditorAction({
                type: 'move-block-to-boundary',
                id: block.id,
                boundaryParams: maybeReorderBoundary,
              });
            }
          }
          // }
        }
      }
      setBlockDragState((blockDragState) => ({
        ...blockDragState,
        stopped: true,
        dragged: false,
        draggedEnoughForAction: false,
      }));
      setReorderBoundary(null);
      setIsDraggingSomethingOnEditor(false);
      const re = renderElements.find((re) => re.type === 'block' && re.block.id === id);
      if (re) {
        releaseElement(
          re.id,
          {
            ...blockDragState,
            stopped: true,
            dragged: false,
            draggedEnoughForAction: false,
          },
          blockDragPositionRef.current
        );
      }
    },
    [innerAreaRect, canvasScrollPosition, blockDragState, sections, boundaries, sectionGrids, renderElements]
  );

  const setEmptySectionContextMenuStateCallback = useRefCallback(
    (sectionId: Section['id'], pageX: number, pageY: number) => {
      setContextMenuState({
        entity: {
          type: 'empty-section',
          sectionId,
        },
        pageX,
        pageY,
      });
    },
    []
  );

  const onAddText = useRefCallback((id: Section['id']) => {
    const blockId = generateBlockId();
    dispatchUserEditorAction({
      type: 'append-block',
      content: {
        type: 'text',
        value: '<h2></h2>',
        horizontalAlignment: 'left',
      },
      id: blockId,
      layoutExtraParams: {},
      sectionId: id,
    });
    selectBlock(blockId);
    setScrollToBlockId(blockId);
  }, []);

  const onAddImage = useRefCallback((id: Section['id']) => {
    const blockId = generateBlockId();
    dispatchUserEditorAction({
      type: 'append-block',
      content: {
        type: 'placeholder',
        placeholderType: 'add-image',
      },
      id: blockId,
      layoutExtraParams: {},
      sectionId: id,
    });
    selectBlock(blockId);
    setScrollToBlockId(blockId);
  }, []);

  return (
    <>
      {sortedRenderElements.map((re, i) => {
        const style = springs[i];
        if (re.type === 'block') {
          const { block: b } = re;
          return (
            <MemoizedBlockViewContainer
              key={re.id}
              journeyUUID={journeyUUID}
              renderElement={re}
              grabbing={b.id === blockDragState.blockId && blockDragState.dragged && !blockDragState.stopped}
              elevated={b.id === blockDragState.blockId && (blockDragState.dragged || blockDragState.stopped)}
              style={style}
              onStart={onStart}
              onDrag={onDrag}
              onStop={onStop}
              layoutMode={'web'}
              renderMode='editor'
              layoutComplete={layoutStage === 'ready'}
            />
          );
        } else if (re.type === 'divider') {
          const dividerRenderElement = re as DividerRenderElement;
          return (
            <MemoizedDividerView
              key={re.id}
              divider={dividerRenderElement}
              style={style}
              grabbing={re.id === blockDragState.blockId && blockDragState.dragged && !blockDragState.stopped}
            />
          );
        } else if (re.type === 'empty-section') {
          return (
            <MemoizedEmptySectionView
              key={re.id}
              section={re.section}
              style={style}
              setContextMenuState={setEmptySectionContextMenuStateCallback}
              onAddText={onAddText}
              onAddImage={onAddImage}
            />
          );
        }
        return null;
      })}
    </>
  );
};

export const MemoizedAnimatedBlocksViewInner = React.memo(AnimatedBlocksViewInner);
